import { db } from '@/db/client';
import { broadcastSessionEvents } from '@/lib/cross-tab-sync';
import { ensureSessionIndexEntry } from '@/lib/sync/session-index';
import type { SessionId } from '@/schemas';
import { downloadRemoteEvents } from './download';
import { mergeRemoteEvents } from './merge';
import { uploadPendingEvents } from './upload';

export interface SyncResult {
  uploadedCount: number;
  downloadedCount: number;
}

export class SyncError extends Error {
  constructor(
    message: string,
    public readonly sessionId: SessionId,
    public readonly type: 'upload' | 'download' | 'full',
    public readonly originalError: unknown,
  ) {
    super(message);
    this.name = 'SyncError';
  }
}

export async function syncSession(sessionId: SessionId): Promise<SyncResult> {
  try {
    const up = await uploadPendingEvents(sessionId);
    const down = await downloadRemoteEvents(sessionId);
    await mergeRemoteEvents(down.events);
    if (down.events.length > 0) {
      broadcastSessionEvents(down.events);
      const { useSessionStore } = await import('@/stores/session-store');
      const sessionStore = useSessionStore.getState();
      if (sessionStore.activeSession?.id === sessionId) {
        await sessionStore.applyRemoteEvents(down.events);
      }
    }
    const metadata = await db.sessionMetadata.get(sessionId);
    const ownerId = metadata?.userId;
    if (ownerId) {
      try {
        await ensureSessionIndexEntry({
          sessionId,
          userId: ownerId,
          createdAt: metadata.createdAt,
          updatedAt: metadata.updatedAt,
          deletedAt: metadata.deletedAt ?? undefined,
        });
      } catch (error) {
        console.warn(`Failed to ensure session index entry for ${sessionId}`, error);
      }
    }
    return { uploadedCount: up.uploaded, downloadedCount: down.events.length };
  } catch (error) {
    throw new SyncError(
      error instanceof Error ? error.message : 'Sync failed',
      sessionId,
      'full',
      error,
    );
  }
}
